import PayoutsTable from "features/affiliation/Payouts/PayoutsTable";
import { PayoutsStatistics } from "features/affiliation/common/types";
import React, { useContext } from "react";
import usePayouts from "features/affiliation/common/hooks/usePayouts";
import Card from "common/Boxes/Card";
import StatisticItem from "features/affiliation/common/StatisticItem";
import { SmartTableLayout } from "common/Layouts/SmartTableLayout";
import styled, { useTheme } from "styled-components";
import Balance from "features/affiliation/common/Balance";
import Button from "common/Buttons/Button";
import PayoutMethods from "features/payouts/PayoutMethods/PayoutMethods";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import { AffiliationContext } from "features/affiliation/Affiliation.context";
import EmptyView from "common/Empty/EmptyView";
import useMedia from "utils/hooks/useMedia";
import { TABLET } from "common/constants";
import {
  AFFILIATION_SMALL_SCREEN,
  PAYOUTS_MOBILE,
} from "features/affiliation/common/constants";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, PayoutMethodSource } from "@shared/types";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

const Payouts = () => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { onCreatePayoutClick } = useContext(AffiliationContext)!;
  const isSmallScreen = useMedia(
    [`(max-width: ${AFFILIATION_SMALL_SCREEN})`],
    [true],
    false
  );
  const isTablet = useMedia([`(max-width: ${TABLET})`], [true], false);
  const isMobile = useMedia([`(max-width: ${PAYOUTS_MOBILE})`], [true], false);

  usePageWrapper({
    showSearch: true,
  });

  const {
    payoutsHistoryQuery,
    payoutMethodsQuery,
    getStatisticValue,
    showEmptyState,
  } = usePayouts();

  const statisticsCards = Object.values(PayoutsStatistics).map(
    (title, index) => (
      <Card key={index} style={{ display: "flex", alignItems: "center" }}>
        <StatisticItem title={t(title)} value={getStatisticValue(title) || 0} />
      </Card>
    )
  );

  const buttonData = {
    buttonText: t("empty.new_payout"),
    buttonIcon: <PlusOutlined />,
    buttonOnClick: onCreatePayoutClick,
  };

  const actionButton = !showEmptyState && (
    <Button
      type="primary"
      onClick={buttonData.buttonOnClick}
      style={{
        marginLeft: 16,
      }}
    >
      {buttonData.buttonIcon}
      {buttonData.buttonText}
    </Button>
  );

  const emptyStateComponent = (
    <EmptyView
      iconKey={EmptyStatesType.Coins}
      title={t("empty.no_payouts")}
      description={t("empty.payouts_description")}
      iconWidth={isMobile ? 100 : 140}
      style={{ padding: isMobile ? "16px 0" : undefined }}
      {...buttonData}
    />
  );

  const payoutsTable = (
    <PayoutsTable
      payouts={payoutsHistoryQuery.data}
      loading={payoutsHistoryQuery.isFetching}
      emptyStateComponent={emptyStateComponent}
    />
  );

  const content = (
    <>
      <TableCard $isSmallScreen={isSmallScreen} $isMobile={isMobile}>
        <SmartTableLayout
          tableComponent={payoutsTable}
          titleOrNode={t("payouts.title")}
          withSearch={true}
          actionTrigger={actionButton}
        />
      </TableCard>
      <BalanceWrapper $isSmallScreen={isSmallScreen} $isMobile={isMobile}>
        <Balance />
      </BalanceWrapper>
      {!isMobile && (
        <PayoutMethods
          payoutMethods={payoutMethodsQuery.data}
          loading={payoutMethodsQuery.isLoading}
          source={PayoutMethodSource.AFFILIATION}
        />
      )}
    </>
  );

  return (
    <LoadingSpinnerOverlay
      isLoading={
        payoutsHistoryQuery.isFetching || payoutMethodsQuery.isFetching
      }
      hideContentWhileLoading={true}
      spinnerMarginTop={50}
      spinnerSize="large"
      wrap={true}
      backgroundColor={theme.colors.layoutBgColor}
      wrapStyle={{ height: "auto" }}
    >
      <Wrapper
        $isSmallScreen={isSmallScreen}
        $isTablet={isTablet}
        $isMobile={isMobile}
      >
        {statisticsCards}
        {isSmallScreen && !isMobile ? (
          <GridWrapper>{content}</GridWrapper>
        ) : (
          content
        )}
      </Wrapper>
    </LoadingSpinnerOverlay>
  );
};

export default Payouts;

const Wrapper = styled.div<{
  $isSmallScreen: boolean;
  $isTablet: boolean;
  $isMobile: boolean;
}>`
  display: grid;
  grid-gap: ${(p) => (p.$isTablet ? 12 : 16)}px;
  margin-top: 32px;
  grid-template-columns: 1fr 1fr 1fr 1fr 2fr;

  ${(p) =>
    p.$isSmallScreen &&
    `
    grid-template-columns: repeat(4, 1fr);
  `};

  ${(p) =>
    p.$isMobile &&
    `
    grid-template-columns: repeat(2, 1fr);
  `};
  @media (max-width: ${phoneMaxWidth}) {
    margin-top: 16px;
  }
`;

const GridWrapper = styled.div`
  grid-column: 1 / 5;
  display: grid;
  grid-gap: 16px;
  grid-template-columns: 5fr 5fr 6fr;
  grid-template-rows: auto 3fr;
`;

const TableCard = styled(Card)<{ $isSmallScreen: boolean; $isMobile: boolean }>`
  grid-column: 1 / 5;
  ${(p) =>
    p.$isSmallScreen &&
    `
    grid-column: 1 / 3;
    grid-row: 1 / 3;
  `}
  ${(p) =>
    p.$isMobile &&
    `
    grid-column: 1 / 3;
    grid-row: 4;
  `}
`;

const BalanceWrapper = styled.div<{
  $isSmallScreen: boolean;
  $isMobile: boolean;
}>`
  ${(p) =>
    !p.$isSmallScreen &&
    `
    grid-column: 5;
    grid-row: 1;
  `};
  ${(p) =>
    p.$isMobile &&
    `
    grid-column: 1 / 3;
    grid-row: 3;
  `}
`;
